import React, { Fragment, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Form from './Form';
import Done from './Done';
import './Order.scss';
import { Helmet } from 'react-helmet';
import Layout from './../Layout';
import * as actions from './Order.actions';
import * as selectors from './Order.selectors';
import * as loginSelectors from './../Login/Login.selectors';
import { stringifyEqualityCheck } from './../../utils';

const Order = (props) => {
    /* State to props */
    const ordering = useSelector(selectors.getOrdering);
    const shipName = useSelector(selectors.getShipName);
    const shipAddress1 = useSelector(selectors.getShipAddress1);
    const shipAddress2 = useSelector(selectors.getShipAddress2);
    const shipCity = useSelector(selectors.getShipCity);
    const shipState = useSelector(selectors.getShipState);
    const shipZip = useSelector(selectors.getShipZip);
    const shipPhone = useSelector(selectors.getShipPhone);
    const shipEmail = useSelector(selectors.getShipEmail);
    const shipNotes = useSelector(selectors.getShipNotes);
    const billName = useSelector(selectors.getBillName);
    const billAddress1 = useSelector(selectors.getBillAddress1);
    const billAddress2 = useSelector(selectors.getBillAddress2);
    const billCity = useSelector(selectors.getBillCity);
    const billState = useSelector(selectors.getBillState);
    const billZip = useSelector(selectors.getBillZip);
    const billPhone = useSelector(selectors.getBillPhone);
    const billEmail = useSelector(selectors.getBillEmail);
    const billPo = useSelector(selectors.getBillPo);
    const billDepartment = useSelector(selectors.getBillDepartment);
    const billCall = useSelector(selectors.getBillCall);
    const billNotes = useSelector(selectors.getBillNotes);
    const sameAddress = useSelector(selectors.getSameAddress);
    const cardNumber = useSelector(selectors.getCardNumber);
    const nameOnCard = useSelector(selectors.getNameOnCard);
    const expirationMonth = useSelector(selectors.getExpirationMonth);
    const expirationYear = useSelector(selectors.getExpirationYear);
    const securityNumber = useSelector(selectors.getSecurityNumber);
    const referenceFiles = useSelector(selectors.getReferenceFiles, stringifyEqualityCheck);
    const orderId = useSelector(selectors.getOrderId);
    const cxmlData = useSelector(loginSelectors.getCxmlData, stringifyEqualityCheck);
    const user = useSelector(loginSelectors.getUser, stringifyEqualityCheck);

    /* Dispatch to props */
    const dispatch = useDispatch();
    const placeOrder = useCallback((orderType) => dispatch(actions.placeOrder(orderType)), [dispatch]);
    const setShipName = useCallback((value) => dispatch(actions.setShipName(value)), [dispatch]);
    const setShipAddress1 = useCallback((value) => dispatch(actions.setShipAddress1(value)), [dispatch]);
    const setShipAddress2 = useCallback((value) => dispatch(actions.setShipAddress2(value)), [dispatch]);
    const setShipCity = useCallback((value) => dispatch(actions.setShipCity(value)), [dispatch]);
    const setShipState = useCallback((value) => dispatch(actions.setShipState(value)), [dispatch]);
    const setShipZip = useCallback((value) => dispatch(actions.setShipZip(value)), [dispatch]);
    const setShipPhone = useCallback((value) => dispatch(actions.setShipPhone(value)), [dispatch]);
    const setShipEmail = useCallback((value) => dispatch(actions.setShipEmail(value)), [dispatch]);
    const setShipNotes = useCallback((value) => dispatch(actions.setShipNotes(value)), [dispatch]);
    const setBillName = useCallback((value) => dispatch(actions.setBillName(value)), [dispatch]);
    const setBillAddress1 = useCallback((value) => dispatch(actions.setBillAddress1(value)), [dispatch]);
    const setBillAddress2 = useCallback((value) => dispatch(actions.setBillAddress2(value)), [dispatch]);
    const setBillCity = useCallback((value) => dispatch(actions.setBillCity(value)), [dispatch]);
    const setBillState = useCallback((value) => dispatch(actions.setBillState(value)), [dispatch]);
    const setBillZip = useCallback((value) => dispatch(actions.setBillZip(value)), [dispatch]);
    const setBillPhone = useCallback((value) => dispatch(actions.setBillPhone(value)), [dispatch]);
    const setBillEmail = useCallback((value) => dispatch(actions.setBillEmail(value)), [dispatch]);
    const setBillPo = useCallback((value) => dispatch(actions.setBillPo(value)), [dispatch]);
    const setBillDepartment = useCallback((value) => dispatch(actions.setBillDepartment(value)), [dispatch]);
    const setBillCall = useCallback((value) => dispatch(actions.setBillCall(value)), [dispatch]);
    const setBillNotes = useCallback((value) => dispatch(actions.setBillNotes(value)), [dispatch]);
    const setSameAddress = useCallback((value) => dispatch(actions.setSameAddress(value)), [dispatch]);
    const setOrderId = useCallback((id) => dispatch(actions.setOrderId(id)), [dispatch]);
    const resetState = useCallback(() => dispatch(actions.resetState()), [dispatch]);
    const setCardNumber = useCallback((value) => dispatch(actions.setCardNumber(value)), [dispatch]);
    const setNameOnCard = useCallback((value) => dispatch(actions.setNameOnCard(value)), [dispatch]);
    const setExpirationMonth = useCallback((value) => dispatch(actions.setExpirationMonth(value)), [dispatch]);
    const setExpirationYear = useCallback((value) => dispatch(actions.setExpirationYear(value)), [dispatch]);
    const setSecurityNumber = useCallback((value) => dispatch(actions.setSecurityNumber(value)), [dispatch]);

    /* componentDidMount */
    useEffect(() => {
        resetState();
        return () => { 
            setOrderId(null);
        };
    }, [setOrderId, resetState]);

    if (user && (user.username === 'utdemo' || user.username === 'personal.demo')) {
        return <Redirect to="/" />;
    }

    let title = 'Please fill in your information below to submit your order into production.';
    if (cxmlData.buyer_cookie != null) {
        title = 'Please checkout to submit your order.';
    }

    let step = null;
    let content = null;
    if (orderId == null || cxmlData.buyer_cookie != null) {
        step = 5;
        content = (
            <Fragment>
                <h2>Order Now!</h2>
                <h3>{title}</h3>
                <Form
                    shipName={shipName}
                    setShipName={setShipName}
                    shipAddress1={shipAddress1}
                    setShipAddress1={setShipAddress1}
                    shipAddress2={shipAddress2}
                    setShipAddress2={setShipAddress2}
                    shipCity={shipCity}
                    setShipCity={setShipCity}
                    shipState={shipState}
                    setShipState={setShipState}
                    shipZip={shipZip}
                    setShipZip={setShipZip}
                    shipPhone={shipPhone}
                    setShipPhone={setShipPhone}
                    shipEmail={shipEmail}
                    setShipEmail={setShipEmail}
                    shipNotes={shipNotes}
                    setShipNotes={setShipNotes}
                    billName={billName}
                    setBillName={setBillName}
                    billAddress1={billAddress1}
                    setBillAddress1={setBillAddress1}
                    billAddress2={billAddress2}
                    setBillAddress2={setBillAddress2}
                    billCity={billCity}
                    setBillCity={setBillCity}
                    billState={billState}
                    setBillState={setBillState}
                    billZip={billZip}
                    setBillZip={setBillZip}
                    billPhone={billPhone}
                    setBillPhone={setBillPhone}
                    billEmail={billEmail}
                    setBillEmail={setBillEmail}
                    billPo={billPo}
                    setBillPo={setBillPo}
                    billDepartment={billDepartment}
                    setBillDepartment={setBillDepartment}
                    billCall={billCall}
                    setBillCall={setBillCall}
                    billNotes={billNotes}
                    setBillNotes={setBillNotes}
                    ordering={ordering}
                    placeOrder={placeOrder}
                    sameAddress={sameAddress}
                    setSameAddress={setSameAddress}
                    referenceFiles={referenceFiles}
                    cxmlData={cxmlData}
                    orderId={orderId}
                    cardNumber={cardNumber}
                    nameOnCard={nameOnCard}
                    expirationMonth={expirationMonth}
                    expirationYear={expirationYear}
                    securityNumber={securityNumber}
                    setCardNumber={setCardNumber}
                    setNameOnCard={setNameOnCard}
                    setExpirationMonth={setExpirationMonth}
                    setExpirationYear={setExpirationYear}
                    setSecurityNumber={setSecurityNumber}
                    user={user}
                />
            </Fragment>
        );
    } else {
        step = 6;
        content = (
            <Fragment>
                <Done
                    orderId={orderId}
                />
            </Fragment>
        );
    }

    return (
        <Layout
            title={<Fragment><i className="fas fa-shopping-cart"></i> Order</Fragment>}
            step={step}
            history={props.history}
        >
            <Helmet><title>Order - UT Physicians</title></Helmet>
            <div className="order-container">
                {content}
            </div>
        </Layout>
    );
};

Order.propTypes = {

};

export default Order;
